import React, { useState } from "react";

import { PracticeCropWithDefaults, isCropNotModelReady, useGetFarms, useTranslation } from "utils";

import { CROP_TYPE_FCPO, PracticeCrop, WithInjectedName } from "models";

import { AlertBanner, Form, Option, Select } from "components";

import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { getCropSelectionOptions } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";

export type BulkCropNameSectionProps = {
  cropType: CROP_TYPE_FCPO;
  practiceCrops: PracticeCropWithDefaults[] | undefined;
  sharedCropId: string | null;
  showResidue: boolean;
  isLoading: boolean;
  disabled: boolean;
  onCropPicked?: (cropId: string) => void;
};

export const BulkCropNameSection = ({ cropType, practiceCrops, sharedCropId, showResidue, isLoading, disabled, onCropPicked }: BulkCropNameSectionProps) => {
  const { t } = useTranslation("allFieldView");
  const form = Form.useFormInstance();
  const { currentFarm } = useGetFarms();
  const [pickedCropId, setPickedCropId] = useState<string | null>(sharedCropId);

  const pickedCrop = practiceCrops?.find((practiceCrop) => practiceCrop.id === pickedCropId);

  // createBulkCropPracticeRequestBody nulls hasResidue out whenever showResidue is false, so switching to a crop
  // that leaves no residue has to update the hidden field — otherwise the save keeps the old crop's residue answer.
  // BulkEditCropForm syncs the same field for the same reason
  const handleCropChange = (nextCropId: string) => {
    setPickedCropId(nextCropId);
    const nextCrop = practiceCrops?.find((practiceCrop) => practiceCrop.id === nextCropId);
    form.setFieldValue([CROP, "showResidue"], !!nextCrop?.residue && !nextCrop?.residueHidden);
    onCropPicked?.(nextCropId);
  };

  return (
    <>
      <Form.Item name={[CROP, "cropType"]} hidden={true} initialValue={cropType}>
        {/* antd requires a valid child; the value is only carried for the request builder */}
        <div />
      </Form.Item>
      <Form.Item name={[CROP, "showResidue"]} hidden={true} initialValue={showResidue}>
        <div />
      </Form.Item>
      <Select
        dataTestId="bulk-crop-name-select"
        label={pickedCropId ? t("cropForm.cropName") : t("cropForm.multipleValues")}
        name={[CROP, "cropId"]}
        initialValue={sharedCropId}
        onChange={handleCropChange}
        showSearch
        isLoading={isLoading}
        disabled={disabled}
        rules={[{ required: true, message: t("cropForm.errorMessages.cropRequired") }]}
      >
        {getCropSelectionOptions(cropType, practiceCrops as WithInjectedName<PracticeCrop>[] | undefined).map((option, index) => (
          <Option data-testid={`bulk-crop-name-option-${index}`} key={option.id} value={option.id}>
            {option.name}
          </Option>
        ))}
      </Select>
      {/* a crop the program's SOC model doesn't cover yet earns no payout, so flag it before the farmer writes it across the
      whole selection — the single-crop form says the same under its own picker */}
      {isCropNotModelReady(pickedCrop, currentFarm?.carbonProgram) && (
        <AlertBanner
          type="info-highlighted"
          iconName="tabler/info-circle"
          dataTestId="bulk-crop-not-model-ready-alert"
          description={t("cropForm.cropNotModelReady")}
          className="margin-top-100"
        />
      )}
    </>
  );
};
